/**
 * Talking to the quote endpoints.
 *
 * A quote can belong to an account or to a browser. The browser's id is set by
 * the server as a first-party cookie and also handed back in the body; we keep
 * a copy here and repeat it in a header, so a guest keeps their quotes even
 * where the cookie does not survive the round trip.
 */
import { sendAuthenticatedRequest } from "../common/functions";
import type { ActivityActor, ActivityEntry } from "../components/ActivityTrail";
import { Cart, CartItem, ShipmentOption } from "../common/models/cart";
import type { RfqLineOption, RfqVendorSuggestion } from "../rfq/api";
import type { ProductInsight } from "../components/SuperUser/superUserApi";

const BASE = `${process.env.REACT_APP_BF_API_BASE_URL}/quotes`;
const PO_BASE = `${process.env.REACT_APP_BF_API_BASE_URL}/purchase-orders`;
const GUEST_KEY = "konoQuoteGuestId";

/**
 * Fired when a quote is saved. The side panel listens: a buyer who has just
 * saved a quote from the cart opens the panel expecting to see it, and a list
 * loaded before they pressed the button would not have it.
 */
export const QUOTES_CHANGED_EVENT = "kono-quotes-changed";

const announceChange = () => {
  window.dispatchEvent(new Event(QUOTES_CHANGED_EVENT));
};

export type QuoteFlowConfig = {
  quote: {
    show: boolean;
    emailPdf: boolean;
    saveWithoutEmail: boolean;
    allowRevisions: boolean;
    showInSidePanel: boolean;
    allowGuests: boolean;
    promptSignIn: boolean;
    validityDays: number;
    includeShipping: boolean;
    termsText: string;
  };
  po: {
    show: boolean;
    askCustomerPoNumber: boolean;
    requireCustomerPoNumber: boolean;
    allowPoUpload: boolean;
    requirePoUpload: boolean;
    uploadMaxMb: number;
    uploadFileTypes: string[];
    requireSignIn: boolean;
  };
  payment: {
    show: boolean;
    creditCard: boolean;
    wireTransfer: boolean;
    terms: boolean;
    termsDays: number;
  };
};

export type QuoteCustomer = {
  name: string;
  company: string;
  email: string;
  phone: string;
};

export type QuoteTotals = {
  productSubtotal: number;
  shippingTotal: number;
  totalPrice: number;
  currency: string;
  itemsMissingPrice: number;
  productSubtotalBeforeDiscount?: number;
  volumeDiscount?: {
    matchingSubtotal: number;
    discountPercent: number;
    discountAmount: number;
    threshold: number;
  } | null;
};

export type Quote = {
  id: string;
  quoteNumber: string;
  version: string;
  reference: string;
  status: "saved" | "sent" | "converted";
  customer: QuoteCustomer;
  cartItems: CartItem[];
  totalShippingOverride?: ShipmentOption;
  totals: QuoteTotals;
  validUntil: string;
  termsText: string;
  includeShipping: boolean;
  createdAt: string;
  emailedTo: string[];
  lastEmailedAt: string | null;
  /** Addresses that can open this quote by its link and act on it. */
  sharedWithEmails?: string[];
  supersedes: string | null;
  supersededBy: string | null;
  orderId: string | null;
  /** Who receives and the delivery instructions, from the operator's freight plan; the checkout starts from them. */
  delivery?: { receiving: { company: string; name: string; phone: string; email: string }; instructions: string } | null;
  /** When a Kono operator sent this version to the customer or marked it sent (operators see this). */
  sentToCustomerAt?: string | null;
  sentToCustomerBy?: string | null;
  /** The customer's business domain; coworkers there share the quote. */
  companyDomain?: string | null;
  createdBy?: ActivityActor | null;
  /** Who did what and when (created, revised, shared, converted, paid...). */
  activity?: ActivityEntry[];
  /** Operators only: a vendor beat this quote after it was generated (see the RFQ). */
  betterPrice?: QuoteBetterPriceFlag | null;
};

/** The "lower price option" flag on a quote; operators see it, the customer hears about it when the new version is sent. */
export type QuoteBetterPriceFlag = {
  status: "open" | "revised" | "dismissed";
  rfqId: string;
  detectedAt: string;
  updatedAt: string;
  lines: number;
  savings: number;
  currency: string;
  vendors: string[];
  revisedQuoteId: string | null;
  revisedQuoteReference: string | null;
};

export type QuoteSummary = Pick<
  Quote,
  | "id"
  | "quoteNumber"
  | "version"
  | "reference"
  | "status"
  | "createdAt"
  | "validUntil"
  | "supersededBy"
  | "lastEmailedAt"
  | "orderId"
> & {
  itemCount: number;
  totalPrice: number;
  currency: string;
  /** Who sent the quote to the signed-in account, and when — set on quotes
   * that appear in the list because they were sent or shared to this email. */
  sentBy: string | null;
  sentAt: string | null;
  companyDomain?: string | null;
  createdBy?: ActivityActor | null;
  lastActivity?: ActivityEntry | null;
  activity?: ActivityEntry[];
  /** A coworker's quote, visible because you share a business domain. */
  byCoworker?: boolean;
  /** Operators only (scope=all): a vendor beat this quote after it was generated. */
  betterPrice?: QuoteBetterPriceFlag | null;
};

const guestId = (): string | null => {
  try {
    return localStorage.getItem(GUEST_KEY);
  } catch {
    return null;
  }
};

const rememberGuest = (id: string | null | undefined) => {
  if (!id) return;
  try {
    localStorage.setItem(GUEST_KEY, id);
  } catch {
    // A browser refusing storage still has the cookie; nothing to do here.
  }
};

const request = async (
  base: string,
  path: string,
  options: { method: string; body?: unknown } = { method: "GET" },
): Promise<Response> => {
  const headers: Record<string, string> = {};
  const id = guestId();
  if (id) headers["X-Kono-Guest"] = id;
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  return sendAuthenticatedRequest(`${base}${path}`, {
    method: options.method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  } as any);
};

const call = (
  path: string,
  options: { method: string; body?: unknown } = { method: "GET" },
): Promise<Response> => request(BASE, path, options);

const poCall = (
  path: string,
  options: { method: string; body?: unknown } = { method: "GET" },
): Promise<Response> => request(PO_BASE, path, options);

// `sendRequest` already throws on anything that is not a 2xx, with the server's
// own message prefixed by the status code.
const jsonOrThrow = async (resp: Response) => resp.json();

/** "403 Quotes are not emailed from here." reads better without the number. */
export const messageOf = (error: unknown): string =>
  String((error as Error)?.message || error || "")
    .replace(/^\d{3}\s+/, "")
    .trim() || "Something went wrong.";

/** What the buyer app may offer. Safe to call signed out. */
export const loadQuoteFlowConfig = async (): Promise<QuoteFlowConfig> =>
  jsonOrThrow(await call("/config"));

export type SaveQuoteInput = {
  cart: Cart;
  customer: QuoteCustomer;
  /** True when the customer asked for it by email; also saves it. */
  deliver: boolean;
};

export const saveQuote = async (input: SaveQuoteInput): Promise<Quote> => {
  const data = await jsonOrThrow(
    await call("", {
      method: "POST",
      body: {
        cartItems: input.cart.cartItems,
        totalShippingOverride: input.cart.totalShippingOverride,
        customer: input.customer,
        deliver: input.deliver,
      },
    }),
  );
  rememberGuest(data.guestId);
  announceChange();
  return data.quote;
};

/** A change to a saved quote: a new revision, never an overwrite. */
export const reviseQuote = async (
  id: string,
  input: Omit<SaveQuoteInput, "deliver">,
): Promise<Quote> => {
  const data = await jsonOrThrow(
    await call(`/${id}/revisions`, {
      method: "POST",
      body: {
        cartItems: input.cart.cartItems,
        totalShippingOverride: input.cart.totalShippingOverride,
        customer: input.customer,
      },
    }),
  );
  announceChange();
  return data.quote;
};

// ---------------------------------------------------------------------------
// Kono operators editing a quote (kono.ai accounts only)
// ---------------------------------------------------------------------------

export type OperatorLineView = {
  index: number;
  productId: string;
  summary: string;
  supplierId: string;
  supplierName: string;
  quotedPrice: { value: number; unit: string; currency: string } | null;
  override: {
    unitPrice?: number;
    currency?: string;
    /** The vendor's cost to Kono per catalogue unit (internal). */
    supplierPrice?: { value: number; unit?: string; currency?: string };
    description?: string;
    leadTime?: string;
    by?: string;
    at?: string;
  } | null;
  /** Where the line ships from (vendor dock or warehouse, or as an operator corrected it in the freight panel). */
  shipFrom?: FreightAddress | null;
  shipFromSource?: "vendor" | "warehouse" | "operator" | "none";
  /** The line's ship-to (the customer's address). */
  shipTo?: FreightAddress | null;
  catalogPrice: { value: number; unit: string; currency: string } | null;
  catalogSummary: string;
  inCatalog: boolean;
  quantityAvailable: { value: number; unit: string } | null;
  leadTimeDays: { min: number | null; max: number | null };
  location: string;
};

export type OperatorQuoteView = {
  editable: boolean;
  /** The customer holds this version: edits now make a new version. */
  sentToCustomer: boolean;
  sentToCustomerAt: string | null;
  sentToCustomerBy: string | null;
  lines: OperatorLineView[];
  shipping: ShipmentOption | null;
};

/* ---- Freight panel (operators): addresses, carrier cost and who pays, per line ---- */

export type FreightDecision = "add_to_quote" | "kono_pays" | "skip";

export type FreightAddress = {
  streetLines: string[];
  city: string;
  postalCode: string;
  countryCode: string;
  stateOrProvinceCode?: string;
};

export type FreightOption = {
  carrier: string;
  vendorId: string;
  serviceCode: string;
  label: string;
  amount: number;
  currency: string;
  estimatedDelivery: string | null;
  vendorShipmentReference: string | null;
  isGuaranteed: boolean;
};

export type FreightPlanLine = {
  index: number;
  productId: string;
  summary: string;
  supplierId: string;
  supplierName: string;
  origin: FreightAddress | null;
  destination: FreightAddress | null;
  originSource: "vendor" | "operator" | "none";
  destinationSource: "customer" | "operator" | "none";
  options: FreightOption[];
  selected: FreightOption | null;
  decision: FreightDecision;
  quotedAt: string | null;
  quoteError: string | null;
  pickup: FreightPickup;
};

export type FreightReceiving = { company: string; name: string; phone: string; email: string };

/** The pickup side of a line, for the carrier: vendor company, the person at the dock, ready day, window, instructions. */
export type FreightPickup = { company: string; name: string; phone: string; email: string; readyDate: string; readyTime: string; closeTime: string; instructions: string };

export type QuoteFreightPlan = {
  lines: FreightPlanLine[];
  receiving: FreightReceiving;
  deliveryInstructions: string;
  customerCharge: number;
  konoPays: number;
  currency: string;
  note: string;
  by: string;
  at: string;
};

export type FreightPanelView = {
  editable: boolean;
  customer: { name: string; company: string; email: string; phone: string };
  plan: QuoteFreightPlan;
  quoteShipping: number | null;
  currency: string;
};

export const loadFreightPanel = async (id: string): Promise<FreightPanelView> => jsonOrThrow(await call(`/${id}/freight`));

/** Live carrier options for one line between the two addresses (nothing saved). */
export const rateFreightLine = async (
  id: string,
  index: number,
  origin: FreightAddress | null,
  destination: FreightAddress | null,
): Promise<{ options: FreightOption[]; error: string | null; quotedAt: string }> =>
  jsonOrThrow(await call(`/${id}/freight/rate`, { method: "POST", body: { index, origin, destination } }));

/** Save the decisions; "add to the quote" lines change what the customer pays (same version or a new one). */
export const saveFreightPlan = async (
  id: string,
  input: { lines: Partial<FreightPlanLine>[]; note: string; receiving: FreightReceiving; deliveryInstructions: string },
): Promise<{ quote: Quote; plan: QuoteFreightPlan; revised: boolean }> => {
  const data = await jsonOrThrow(await call(`/${id}/freight`, { method: "POST", body: input }));
  announceChange();
  return data;
};

/** The operator sent the quote to the customer outside kono.ai: record it. */
export const markQuoteSentToCustomer = async (id: string, how: string): Promise<{ quote: Quote; alreadySent: boolean }> => {
  const data = await jsonOrThrow(await call(`/${id}/mark-sent`, { method: "POST", body: { how } }));
  announceChange();
  return data;
};

export const loadOperatorQuoteView = async (id: string): Promise<OperatorQuoteView> =>
  jsonOrThrow(await call(`/${id}/operator`));

export type CatalogOption = {
  productId: string;
  summary: string;
  supplierId: string;
  supplierName: string;
  price: { value: number; unit: string; currency: string } | null;
  quantityAvailable: { value: number; unit: string } | null;
  leadTimeDays: { min: number | null; max: number | null };
  location: string;
  current: boolean;
};

/** Items that could take a line's place (line >= 0) or be added (line = -1, q required). */
export const loadCatalogOptions = async (id: string, line: number, q: string): Promise<CatalogOption[]> => {
  const params = new URLSearchParams({ line: String(line), q });
  const data = await jsonOrThrow(await call(`/${id}/catalog-options?${params.toString()}`));
  return data.options || [];
};

export type OperatorLineEdit = {
  index: number;
  remove?: boolean;
  productId?: string | null;
  /** Take the same catalogue item as it is now (price, size, description). */
  refresh?: boolean;
  quantity?: { value: number; unit: string } | null;
  unitPrice?: number | null;
  /** The final amount for the line; the server works out the unit price from it. Wins over unitPrice. */
  lineTotal?: number | null;
  currency?: string | null;
  /** What the vendor charges Kono for the line (internal; never printed). */
  supplierPrice?: number | null;
  leadTime?: string | null;
  description?: string | null;
};

export type OperatorRevisionInput = {
  lines?: OperatorLineEdit[];
  added?: { productId: string; quantity: { value: number; unit: string }; unitPrice?: number | null; lineTotal?: number | null; currency?: string | null; leadTime?: string | null; description?: string | null }[];
  shipping?: { amount: number; currency?: string | null; label?: string | null } | { clear: true } | null;
  customer?: Partial<QuoteCustomer>;
  note?: string | null;
};

/**
 * The operator's edits: saved into the same version while the customer does
 * not hold it yet (revised: false), as the next revision once they do.
 */
export const saveOperatorRevision = async (
  id: string,
  input: OperatorRevisionInput,
): Promise<{ quote: Quote; changes: string[]; revised: boolean }> => {
  const data = await jsonOrThrow(await call(`/${id}/operator-revision`, { method: "POST", body: input }));
  announceChange();
  return data;
};

export const listQuotes = async (
  /** "all": every customer's quotes; honored only for kono.ai accounts. */
  scope: "mine" | "all" = "mine",
): Promise<{
  quotes: QuoteSummary[];
  asGuest: boolean;
}> => {
  const data = await jsonOrThrow(await call(scope === "all" ? "?scope=all" : ""));
  rememberGuest(data.guestId);
  return { quotes: data.quotes, asGuest: data.asGuest };
};

export const loadQuote = async (id: string): Promise<Quote> =>
  (await jsonOrThrow(await call(`/${id}`))).quote;

// ---------------------------------------------------------------------------
// Kono-only: the sourcing behind a quote
// ---------------------------------------------------------------------------

export type QuoteVendorRequestStatus =
  | "queued"
  | "sent"
  | "responded"
  | "declined"
  | "closed";

export type QuoteVendorRequestView = {
  id: string;
  supplierId: string;
  supplierName: string;
  emails: string[];
  status: QuoteVendorRequestStatus;
  sentAt: string;
  lastEmailAt: string;
  followUpsSent: number;
  nextFollowUpAt: string | null;
  respondedAt: string | null;
  responseSummary: string;
  productIds: string[];
  queuedUntil: string | null;
  declineKind?: string | null;
  declineReason?: string | null;
  responseText?: string | null;
};

export type QuoteLineVerdict = "exact" | "close" | "unavailable";

export type QuoteItemSourcing = {
  index: number;
  summary: string;
  productId: string | null;
  vendor: { supplierId: string | null; name: string | null };
  vendorRequest: QuoteVendorRequestView | null;
  /** The item as the super-user panel shows it; null if no longer in inventory. */
  insight: ProductInsight | null;
  line: {
    id: string;
    material: string;
    shape: string;
    standard: string;
    verdict: QuoteLineVerdict;
  } | null;
  options: RfqLineOption[];
  suggestions: RfqVendorSuggestion[];
  vendorRequests: QuoteVendorRequestView[];
};

export type QuoteLineSourcing = {
  line: {
    id: string;
    material: string;
    shape: string;
    standard: string;
    description: string;
  };
  verdict: QuoteLineVerdict;
  options: RfqLineOption[];
  suggestions: RfqVendorSuggestion[];
  vendorRequests: QuoteVendorRequestView[];
};

export type QuoteSourcing = {
  rfq: {
    id: string;
    status: string;
    submittedAt: string | null;
    link: string;
    customerEmail: string;
  } | null;
  items: QuoteItemSourcing[];
  otherLines: QuoteLineSourcing[];
};

/** Kono only (404 for anyone else). Live matching: takes a few seconds. */
export const loadQuoteSourcing = async (id: string): Promise<QuoteSourcing> =>
  jsonOrThrow(await call(`/${id}/sourcing`));

/**
 * Delete a quote and all its revisions. Only the quote's owner or a kono.ai
 * account may; a quote already converted to an order is refused (409).
 */
export const deleteQuote = async (id: string): Promise<void> => {
  await jsonOrThrow(await call(`/${id}`, { method: "DELETE" }));
  announceChange();
};

/**
 * The customer switches a quote line to one of the alternatives offered on
 * it; the next version of the quote comes back with that item on the line.
 */
export const chooseQuoteAlternative = async (
  id: string,
  input: { lineProductId: string; productId: string },
): Promise<Quote> => {
  const data = await jsonOrThrow(
    await call(`/${id}/choose-alternative`, { method: "POST", body: input }),
  );
  return data.quote;
};

export const emailQuote = async (
  id: string,
  to: string,
  edited?: { subject: string; text: string; cc: string[] },
): Promise<void> => {
  await jsonOrThrow(
    await call(`/${id}/email`, { method: "POST", body: { to, ...(edited || {}) } }),
  );
};

/** The quote email as it would go out (Kono operators), to edit before sending. */
export type QuoteEmailDraft = { to: string; cc: string[]; subject: string; text: string; copyTo: string[] };
export const loadQuoteEmailDraft = async (id: string): Promise<QuoteEmailDraft> =>
  jsonOrThrow(await call(`/${id}/email-draft`));

/** Every email around the quote (all revisions) and the RFQ it answers, oldest first (Kono operators). */
export type QuoteEmailThreadEntry = {
  at: string;
  direction: "out" | "in";
  source: "rfq-email" | "quote-email" | "quote-share" | "mailbox" | "held";
  kind: string;
  from: string;
  to: string[];
  cc: string[];
  subject: string;
  text: string;
  by: string;
  quoteReference: string | null;
  tracking: { delivered: boolean; opens: number; clicks: number; bounced: boolean } | null;
  held: boolean;
};
export type QuoteEmailThread = { rfqId: string | null; quoteReferences: string[]; entries: QuoteEmailThreadEntry[] };
export const loadQuoteEmailThread = async (id: string): Promise<QuoteEmailThread> =>
  jsonOrThrow(await call(`/${id}/emails`));

/**
 * Grant more email addresses access to the quote by its link; each new
 * address is emailed the link. Signed in with a shared address, the recipient
 * can review the quote, purchase it, or convert it to a PO.
 */
export const shareQuote = async (
  id: string,
  emails: string[],
): Promise<{ sharedWithEmails: string[]; emailed: boolean }> =>
  jsonOrThrow(await call(`/${id}/share`, { method: "POST", body: { emails } }));

/**
 * Downloads the PDF. Fetched rather than linked because the request carries the
 * sign-in token, which an anchor cannot.
 */
export const downloadQuotePdf = async (quote: {
  id: string;
  reference: string;
}): Promise<void> => {
  const resp = await call(`/${quote.id}/pdf`);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${quote.reference}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/** Hands this browser's quotes to the account that just signed in. */
export const claimGuestQuotes = async (): Promise<number> => {
  if (!guestId()) return 0;
  try {
    const data = await (await call("/claim", { method: "POST" })).json();
    return data.claimed || 0;
  } catch {
    // Nothing to claim, or quoting is off. Neither is worth troubling anyone.
    return 0;
  }
};

/**
 * Fired when a purchase order is raised, changed or paid, for the same reason
 * as `QUOTES_CHANGED_EVENT`: the side panel is usually already on screen.
 */
export const PURCHASE_ORDERS_CHANGED_EVENT = "kono-purchase-orders-changed";

const announcePoChange = () => {
  window.dispatchEvent(new Event(PURCHASE_ORDERS_CHANGED_EVENT));
};

export type PoAttachment = {
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
};

export type PurchaseOrder = {
  id: string;
  poNumber: string;
  customerPoNumber: string;
  quoteId: string;
  quoteReference: string;
  quoteValidUntil: string;
  customer: QuoteCustomer;
  cartItems: CartItem[];
  totalShippingOverride?: ShipmentOption;
  totals: QuoteTotals;
  attachment: PoAttachment | null;
  status: "awaiting_payment" | "ordered" | "cancelled";
  orderId: string | null;
  paymentMethod: string | null;
  createdAt: string;
  companyDomain?: string | null;
  createdBy?: ActivityActor | null;
  activity?: ActivityEntry[];
};

export type PurchaseOrderSummary = Pick<
  PurchaseOrder,
  | "id"
  | "poNumber"
  | "customerPoNumber"
  | "quoteReference"
  | "status"
  | "orderId"
  | "createdAt"
> & {
  itemCount: number;
  totalPrice: number;
  currency: string;
  hasAttachment: boolean;
  createdBy?: ActivityActor | null;
  lastActivity?: ActivityEntry | null;
  activity?: ActivityEntry[];
};

/** A file picked in the browser, ready to travel as JSON. */
export type FileUpload = {
  filename: string;
  contentType: string;
  /** Base64, without the data-URL prefix. */
  data: string;
};

export const readFileForUpload = (file: File): Promise<FileUpload> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("That file could not be read."));
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        data: comma === -1 ? result : result.slice(comma + 1),
      });
    };
    reader.readAsDataURL(file);
  });

export const raisePurchaseOrder = async (input: {
  quoteId: string;
  customerPoNumber: string;
  attachment?: FileUpload | null;
}): Promise<PurchaseOrder> => {
  const data = await jsonOrThrow(
    await poCall("", {
      method: "POST",
      body: {
        quoteId: input.quoteId,
        customerPoNumber: input.customerPoNumber,
        attachment: input.attachment || undefined,
      },
    }),
  );
  rememberGuest(data.guestId);
  announcePoChange();
  announceChange();
  return data.purchaseOrder;
};

export const listPurchaseOrders = async (): Promise<{
  purchaseOrders: PurchaseOrderSummary[];
  asGuest: boolean;
}> => {
  const data = await jsonOrThrow(await poCall(""));
  rememberGuest(data.guestId);
  return { purchaseOrders: data.purchaseOrders, asGuest: data.asGuest };
};

export const loadPurchaseOrder = async (id: string): Promise<PurchaseOrder> =>
  (await jsonOrThrow(await poCall(`/${id}`))).purchaseOrder;

/** Their PO number or document, added or replaced after the order was raised. */
export const updatePurchaseOrder = async (
  id: string,
  changes: { customerPoNumber?: string; attachment?: FileUpload | null },
): Promise<PurchaseOrder> => {
  const data = await jsonOrThrow(
    await poCall(`/${id}`, {
      method: "PATCH",
      body: {
        customerPoNumber: changes.customerPoNumber,
        attachment: changes.attachment || undefined,
      },
    }),
  );
  announcePoChange();
  return data.purchaseOrder;
};

export const downloadPoAttachment = async (po: {
  id: string;
  attachment: PoAttachment | null;
}): Promise<void> => {
  if (!po.attachment) return;
  const resp = await poCall(`/${po.id}/attachment`);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = po.attachment.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export type PoCheckout = {
  orderId: string;
  clientSecret?: string;
  paymentMethod: "credit_card" | "wire_transfer";
  wireTransferRecipientEmail?: string;
};

/**
 * Pays a purchase order. Goes to `/create-order`, the same endpoint an ordinary
 * checkout uses, so a PO order lands in Sales > Orders like any other; the
 * server takes the lines and the price from the stored PO rather than from here.
 */
export const payPurchaseOrder = async (
  po: PurchaseOrder,
  paymentMethod: "credit_card" | "wire_transfer",
): Promise<PoCheckout> => {
  const id = guestId();
  const resp = await sendAuthenticatedRequest(
    `${process.env.REACT_APP_BF_API_BASE_URL}/create-order`,
    {
      method: "POST",
      headers: id ? { "X-Kono-Guest": id } : undefined,
      jsonBody: {
        purchaseOrderId: po.id,
        paymentMethod,
        cartItems: po.cartItems,
        name: po.customer.name,
        company: po.customer.company,
        email: po.customer.email,
        phone: po.customer.phone,
      },
    } as any,
  );
  const data = await resp.json();
  announcePoChange();
  return data;
};
